import type {
  PickerSource,
  ProviderVariant,
} from 'slices/aiConversations/pickerTypes'

import type {
  PickerRow,
  PickerRowKind,
  PickerRowsPage,
  PriceRange,
  ProviderVariantWire,
  SnowflakeRecordWire,
  SnowflakeRowsWire,
  StoreUidRow,
  UidGroupRow,
  UidsLookup,
} from '../canonicalPicker.types'
import { toProviderVariant } from '../canonicalPicker.types'
import { fieldOf, isString } from './guards'
import { STORE_UID_COLUMNS, UID_GROUP_FIELD } from './query'

export const uidGroupRowId = (provider: string, uidGroup: string): string =>
  `${provider}:${uidGroup}`

const textOf = (record: SnowflakeRecordWire, field: string): string => {
  const value = record[field]
  return value === null || value === undefined ? '' : String(value)
}

const jsonArrayOf = (
  record: SnowflakeRecordWire,
  field: string | null
): unknown[] => {
  if (!field) return []
  try {
    const parsed: unknown = JSON.parse(textOf(record, field))
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

const jsonStringsOf = (record: SnowflakeRecordWire, field: string): string[] =>
  jsonArrayOf(record, field)
    .filter(value => isString(value) || typeof value === 'number')
    .map(String)

const isProviderVariantWire = (value: unknown): value is ProviderVariantWire =>
  isString(fieldOf(value, 'provider')) &&
  isString(fieldOf(value, 'provider_uid'))

const variantsOf = (
  record: SnowflakeRecordWire,
  field: string | null
): ProviderVariant[] =>
  jsonArrayOf(record, field)
    .filter(isProviderVariantWire)
    .map(toProviderVariant)

const storeUidsOf = (record: SnowflakeRecordWire): string[] =>
  jsonStringsOf(record, UID_GROUP_FIELD.storeUids)

const locationIdsOf = (record: SnowflakeRecordWire, field: string): number[] =>
  jsonArrayOf(record, field).map(Number).filter(Number.isInteger)

const centsOf = (record: SnowflakeRecordWire, field: string): number | null => {
  const text = textOf(record, field)
  const cents = Number(text)
  return text === '' || !Number.isFinite(cents) ? null : cents
}

const priceRangeOf = (record: SnowflakeRecordWire): PriceRange | null => {
  const minCents = centsOf(record, UID_GROUP_FIELD.priceMin)
  const maxCents = centsOf(record, UID_GROUP_FIELD.priceMax)
  return minCents === null || maxCents === null ? null : { minCents, maxCents }
}

const rowOf = (
  record: SnowflakeRecordWire,
  source: PickerSource,
  kind: PickerRowKind,
  withDetails: boolean
): PickerRow => {
  const key = textOf(record, source.keyField)
  const name = textOf(record, source.valueField) || key

  if (kind === 'product') {
    return {
      kind,
      id: key,
      name,
      variants: withDetails ? variantsOf(record, source.variantsField) : null,
    }
  }

  const provider = textOf(record, UID_GROUP_FIELD.provider)
  return {
    kind,
    id: uidGroupRowId(provider, key),
    provider,
    uidGroup: key,
    name,
    isGlobal: jsonStringsOf(record, UID_GROUP_FIELD.globalUids).length > 0,
    canonicalId: textOf(record, UID_GROUP_FIELD.canonicalId),
    canonicalName: textOf(record, UID_GROUP_FIELD.canonicalName) || name,
    locationIds: locationIdsOf(record, UID_GROUP_FIELD.locationIds),
    storeUids: UID_GROUP_FIELD.storeUids in record ? storeUidsOf(record) : null,
    priceRange: withDetails ? priceRangeOf(record) : null,
    // Read whenever asked for: a lookup by key or by product
    productIds:
      UID_GROUP_FIELD.canonicalIds in record
        ? jsonStringsOf(record, UID_GROUP_FIELD.canonicalIds)
        : null,
  }
}

/** A scoped source holds a row per parent; one product's rows add up its variants */
const mergedRowOf = (existing: PickerRow, row: PickerRow): PickerRow =>
  existing.kind === 'product' &&
  row.kind === 'product' &&
  existing.variants &&
  row.variants
    ? { ...existing, variants: [...existing.variants, ...row.variants] }
    : existing

export const pageOf = (
  response: SnowflakeRowsWire,
  source: PickerSource,
  kind: PickerRowKind,
  withDetails: boolean
): PickerRowsPage => {
  const rows = new Map<string, PickerRow>()
  response.records.forEach(record => {
    const row = rowOf(record, source, kind, withDetails)
    const existing = rows.get(row.id)
    rows.set(row.id, existing ? mergedRowOf(existing, row) : row)
  })
  return {
    rows: Array.from(rows.values()),
    totalCount: response.pagination.total_count,
  }
}

export const isUidGroupRow = (row: PickerRow): row is UidGroupRow =>
  row.kind === 'uidGroup'

const storeUidRowOf = (version: UidGroupRow, uid: string): StoreUidRow => ({
  kind: 'storeUid',
  id: `storeUid:${version.provider}:${uid}`,
  provider: version.provider,
  uid,
  name: version.canonicalName,
  storeName: version.name,
  canonicalId: version.canonicalId,
  productIds: version.productIds ?? [version.canonicalId],
  locationIds: null,
})

/**
 * The groups holding each uid, in the order the uids came, and the uids no
 * group holds. A group holds a uid that keys it, or one of its store UIDs
 * that `isSold` says sells where the merchant can see; with no such check,
 * any of its store UIDs.
 */
export const uidsLookupOf = (
  uids: string[],
  found: UidGroupRow[],
  isSold: (provider: string, uid: string) => boolean = () => true
): UidsLookup => {
  const groups = new Map<string, UidGroupRow>()
  const unmatchedUids: string[] = []
  uids.forEach(uid => {
    const holding = found.filter(
      group =>
        group.uidGroup === uid ||
        (group.storeUids?.includes(uid) && isSold(group.provider, uid))
    )
    if (holding.length === 0) unmatchedUids.push(uid)
    holding.forEach(group => groups.set(group.id, group))
  })
  return { groups: Array.from(groups.values()), unmatchedUids }
}

/**
 * A group's store UIDs as its details source lists them, by store name, each
 * at the locations it shares with the group. A column the details source
 * doesn't name falls back on the group's.
 */
export const storeUidRowsOf = (
  response: SnowflakeRowsWire,
  group: UidGroupRow
): StoreUidRow[] => {
  const rows = new Map<string, StoreUidRow>()
  const groupLocations = new Set(group.locationIds)
  response.records.forEach(record => {
    const uid = textOf(record, STORE_UID_COLUMNS.uid)
    if (!uid) return
    const canonicalId =
      textOf(record, STORE_UID_COLUMNS.canonicalId) || group.canonicalId
    const row: StoreUidRow = {
      ...storeUidRowOf(group, uid),
      name:
        textOf(record, STORE_UID_COLUMNS.canonicalName) || group.canonicalName,
      storeName: textOf(record, STORE_UID_COLUMNS.name) || group.name,
      canonicalId,
      productIds: [canonicalId],
      // Only where it sells as part of this global, so it never shows more locations than the global
      locationIds: locationIdsOf(record, STORE_UID_COLUMNS.locationIds).filter(
        id => groupLocations.has(id)
      ),
    }
    rows.set(row.id, row)
  })
  return Array.from(rows.values()).sort(
    (a, b) =>
      a.storeName.localeCompare(b.storeName) || a.uid.localeCompare(b.uid)
  )
}

/** The UID groups each product's variants name, by group row id: the products that name it */
export const productsByVariantGroupOf = (
  rows: PickerRow[]
): Map<string, string[]> => {
  const named = new Map<string, string[]>()
  rows.forEach(row => {
    if (row.kind !== 'product') return
    ;(row.variants ?? []).forEach(({ provider, uidGroup }) => {
      if (!uidGroup) return
      const id = uidGroupRowId(provider, uidGroup)
      const products = named.get(id) ?? []
      if (!products.includes(row.id)) named.set(id, [...products, row.id])
    })
  })
  return named
}

/**
 * A group reaches the products it is filed under, and every product whose
 * variants name it, so a group filed under a merged product's other id still
 * counts as a version of the product the merchant picked.
 */
export const groupReaching = (
  group: UidGroupRow,
  productIds: string[]
): UidGroupRow => ({
  ...group,
  productIds: Array.from(
    new Set([...(group.productIds ?? [group.canonicalId]), ...productIds])
  ),
})

/** The (provider, uid) pairs a details source lists, as `provider:uid` */
export const soldUidsOf = (response: SnowflakeRowsWire): Set<string> =>
  new Set(
    response.records.map(
      record =>
        `${textOf(record, STORE_UID_COLUMNS.provider)}:${textOf(
          record,
          STORE_UID_COLUMNS.uid
        )}`
    )
  )
